import { ref, computed, h } from 'vue';
import http from '@monolith/legacy/services/api/http';
import { useDrawer } from '@bc/shared';
import { useBreakpoints } from '@monolith/legacy/modules/design-system-candidates';
import { captureBrandsException } from '@core/plugins/sentry/helper';
import VideoLibraryDrawer from '../components/video-library-drawer/video-library-drawer.vue';
import type { Video, VideoResponse } from '../types';

// Shared state across all instances
const videos = ref<Video[]>([]);
const isLoading = ref(false);
const pagination = ref<VideoResponse['meta']['page']>();
const totalVideoCount = ref(0);

export function useVideoLibrary(brandUuid: string) {
  const drawer = useDrawer();
  const { isBelowLg } = useBreakpoints();

  const videoCount = computed(() => totalVideoCount.value);

  const fetchVideos = async (page = 1, query = ''): Promise<void> => {
    if (!brandUuid) {
      return;
    }

    isLoading.value = true;

    try {
      const response = await http().get<VideoResponse>('/api/internal/v1/videos', {
        params: {
          'filter[brandUuid]': brandUuid,
          'filter[query]': query,
          'page[limit]': 20,
          'page[offset]': (page - 1) * 20,
        },
      });

      videos.value = response.data.data || [];
      pagination.value = response.data.meta?.page;
      totalVideoCount.value = response.data.meta?.page?.total || 0;
    } catch (e) {
      captureBrandsException(e);
      videos.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const addVideo = async (youtubeUrl: string, displayContext: string): Promise<boolean> => {
    if (!brandUuid || !youtubeUrl.trim()) {
      return false;
    }

    isLoading.value = true;

    try {
      await http().post('/api/internal/v1/videos/upload', {
        external_url: youtubeUrl,
        display_context: displayContext,
      });

      await fetchVideos();
      return true;
    } catch (e) {
      captureBrandsException(e);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteVideo = async (videoId: string): Promise<boolean> => {
    if (!brandUuid || !videoId) {
      return false;
    }

    isLoading.value = true;

    try {
      await http().delete(`/api/internal/v1/videos/${videoId}`);
      await fetchVideos();
      return true;
    } catch (e) {
      captureBrandsException(e);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const handlePageChange = (page: number, query: string) => {
    void fetchVideos(page, query);
  };

  const handleSearch = (query: string) => {
    void fetchVideos(1, query);
  };

  const handleVideoAdd = async (data: { url: string; displayContext: string }) => {
    await addVideo(data.url, data.displayContext);
  };

  const handleItemDelete = async (video: Video) => {
    await deleteVideo(video.id);
  };

  const createVideoLibraryComponent = () =>
    h(VideoLibraryDrawer, {
      items: videos.value,
      pagination: pagination.value,
      isLoading: isLoading.value,
      isUploading: false,
      totalVideoCount: totalVideoCount.value,
      onPageChange: handlePageChange,
      onSearch: handleSearch,
      onVideoAdd: handleVideoAdd,
      onItemDelete: handleItemDelete,
    });

  const drawerOptions = {
    right: true,
    width: isBelowLg.value ? '100%' : '60%',
  };

  const openVideoLibraryDrawer = async () => {
    try {
      drawer.openDrawer(createVideoLibraryComponent, drawerOptions);
      await fetchVideos();
    } catch (e) {
      captureBrandsException(e);
    }
  };

  const closeVideoLibraryDrawer = () => {
    drawer.closeDrawer();
  };

  return {
    videos,
    videoCount,
    isLoading,
    fetchVideos,
    addVideo,
    deleteVideo,
    openVideoLibraryDrawer,
    closeVideoLibraryDrawer,
  };
}
